import React, { useEffect, useState } from "react";
import "../css/salesHistory.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ You must import it like this
import { printReceipt } from "../utils/printReceipt"; // Adjust path if needed
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useConfirm } from "../contexts/ConfirmContext"; // adjust path if needed
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";



// Normalize API base: strip trailing slashes AND a trailing `/api` if present
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");

// Helper to always produce `/api/...` regardless of how API_BASE is set
const api = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;


const SalesHistory = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState("All");
  const today = new Date().toISOString().split("T")[0];
const [dateFilter, setDateFilter] = useState(today);

  const [viewMine, setViewMine] = useState(false);
  const [appliedPaymentFilter, setAppliedPaymentFilter] = useState("All");
  const [appliedDateFilter, setAppliedDateFilter] = useState("");
  
  const [customerDatabase, setCustomerDatabase] = useState([]);
  const confirm = useConfirm();
const [searchTerm, setSearchTerm] = useState("");
const [currentPage, setCurrentPage] =
  useState(1);
const fetchSales = async () => {
  try {
    setLoading(true);

    const headers = { ...authHeader() };
    if (!headers.Authorization) {
      toast.error("Please sign in again.");
      setSales([]);
      return;
    }

    // Non-admins: backend auto-scopes to their own sales.
    // Admins: viewMine -> include legacy NULL (backend implements (cashier_id = me OR NULL) when scope=mine)
    const qs = isAdmin() && viewMine ? "?scope=mine" : "";
    const url = api(`/sales${qs}`);


    console.log("🧠 Fetching sales from:", url);
console.log("🔑 Headers being sent:", headers);



const res = await fetch(url, { headers });

// Log status before parsing
console.log("📦 Response status:", res.status);

const text = await res.clone().text();


if (!res.ok) {
  console.error("GET /api/sales failed:", res.status, text);
  toast.error(`Failed to load sales (${res.status}).`);
  setSales([]);
  return;
}

let data;
try {
  data = JSON.parse(text);
} catch (err) {
  console.error("❌ JSON parse error:", err);
  toast.error("Invalid JSON returned by backend.");
  setSales([]);
  return;
}


setSales(Array.isArray(data) ? data : []);

  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchSales();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [viewMine]);

  
// --- auth helpers ---
// --- auth helpers ---
const safeJson = (s) => { try { return JSON.parse(s); } catch { return null; } };

// --- auth helpers (copy this over your current ones) ---
const getAuth = () => {
  try {
    const raw =
      localStorage.getItem("rcs_auth") ||
      sessionStorage.getItem("rcs_auth") ||
      localStorage.getItem("auth") ||
      sessionStorage.getItem("auth");

    if (!raw) return {};
    const data = JSON.parse(raw);
    return {
      access_token: data.access_token || data.token || data.jwt || null,
      user: data.user || data.profile || null,
    };
  } catch (err) {
    console.error("Error parsing auth data:", err);
    return {};
  }
};

const authHeader = () => {
  const { access_token } = getAuth();
  if (!access_token) {
    console.warn("⚠️ No token found in storage");
    return {};
  }
  return { Authorization: `Bearer ${access_token}` };
};

const currentUser = () => getAuth()?.user || null;

const isAdmin = () => {
  const u = currentUser();
  if (!u) return false;
  if (typeof u.role === "string") return u.role.toLowerCase() === "admin";
  if (Array.isArray(u.roles)) return u.roles.map(String).map(r => r.toLowerCase()).includes("admin");
  return false;
};

const displayCashier = (s) =>
  (s.cashier_name && s.cashier_name !== "Unknown")
    ? s.cashier_name
    : (s.cashier_email || (s.cashier_id ? `#${s.cashier_id}` : "Unknown"));



useEffect(() => {
  fetch(api("/customers"), { headers: { ...authHeader() } })
     .then(res => res.json())
     .then(data => setCustomerDatabase(data))
     .catch(err => console.error("Failed to load customers", err));
}, []);

const handleExportPDF = () => {
  const doc = new jsPDF();

  const reportType =
    appliedPaymentFilter === "All"
      ? "Full Sales Report"
      : `${appliedPaymentFilter} Report`;

  const reportDate =
    appliedDateFilter !== ""
      ? new Date(appliedDateFilter).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "All Dates";

  doc.setFontSize(14);
  doc.text(`${reportType} for ${reportDate}`, 14, 10);

const tableColumn = [
  "Receipt No.",
  "Customer",
  "Payment",
  ...(isAdmin() ? ["Cashier"] : []),
  "Items",
  "Subtotal",
  "Discount",
  "Total",
  "Date",
  "Time",
];


const tableRows = filteredSales.map((sale) => {
  const itemText =
    sale.items
      ?.split(",")
      .map((item) => {
        const parts = item.split(":");
        if (parts.length === 3) {
          const [name, quantity, price] = parts;
          return `${name.trim()} x${quantity.trim()} @ $${parseFloat(price).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        }
        if (parts.length === 2) {
          const [name, price] = parts;
          return `${name.trim()} x1 @ $${parseFloat(price).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        }
        return item.trim();
      })
      .join(" | ") || "—";

  const base = [
    sale.payment_method === "Cash"
      ? `RCSCS${sale.receipt_no.replace("RCS", "")}`
      : `RCSCR${sale.receipt_no.replace("RCS", "")}`,
    sale.customer_name || "Unknown",
    sale.payment_method,
  ];

  if (isAdmin()) {
    const cashier =
      sale.cashier_name ||
      sale.cashier_email ||
      (sale.cashier_id ? `#${sale.cashier_id}` : "Unknown");
    base.push(cashier);
  }

  return [
    ...base,
    itemText,
    `$${parseFloat(sale.subtotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    `-$${parseFloat(sale.discount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    `$${parseFloat(sale.total).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    new Date(sale.date_of_transaction).toLocaleDateString("en-US"),
    new Date(sale.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  ];
});


  // ✅ Totals
  const totalSubtotal = filteredSales.reduce(
    (sum, sale) => sum + parseFloat(sale.subtotal),
    0
  );
  const totalTotal = filteredSales.reduce(
    (sum, sale) => sum + parseFloat(sale.total),
    0
  );


  // ✅ Add totals row
const totalsColSpan = isAdmin() ? 5 : 4; // Receipt, Customer, Payment, [Cashier], Items

tableRows.push([
  { content: "TOTALS", colSpan: totalsColSpan, styles: { halign: "right", fontStyle: "bold" } },
  `$${totalSubtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
  "",
  `$${totalTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
  "",
  "",
]);

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: 20,
  });

  doc.save("sales_history.pdf");
};


const normalize = (v = "") =>
  v.toString().toLowerCase().replace(/\s+/g, "");

const extractNumber = (v = "") =>
  v.toString().replace(/\D/g, "");

const filteredSales = sales.filter((sale) => {
  const term = normalize(searchTerm);

  // 🔒 SEARCH MODE — HARD OVERRIDE
  if (term) {
    const saleReceipt = normalize(sale.receipt_no || ""); // e.g. rcs1430

    // Allow:
    // 1) Exact full receipt match (RCS1430)
    // 2) Exact numeric match (1430)
    return (
      term === saleReceipt ||
      extractNumber(term) === extractNumber(saleReceipt)
    );
  }

  // 🧠 NORMAL FILTER MODE (NO SEARCH)
  const rawDate = sale.date_of_transaction || sale.created_at || "";
  const saleDate = String(rawDate).split("T")[0];

  const matchesPayment =
    appliedPaymentFilter === "All" ||
    sale.payment_method === appliedPaymentFilter;

  const matchesDate =
    !appliedDateFilter || saleDate === appliedDateFilter;

  return matchesPayment && matchesDate;
});



const handleExportCSV = () => {
  // 🧾 Headers (Cashier only for admins)
  const headers = [
    "Receipt No.",
    "Customer",
    "Payment",
    ...(isAdmin() ? ["Cashier"] : []),
    "Items",
    "Subtotal",
    "Discount",
    "Total",
    "Date",
    "Time",
  ];

  const rows = filteredSales.map((sale) => {
    const itemText = sale.items
      ?.split(",")
      .map((item) => {
        const parts = item.split(":");
        if (parts.length === 3) {
          const [name, quantity] = parts;
          return `${name.trim()} x${quantity.trim()}`;
        }
        if (parts.length === 2) {
          const [name] = parts;
          return `${name.trim()} x1`;
        }
        return item.trim();
      })
      .join(" | ");

    const receipt =
      sale.payment_method === "Cash"
        ? `RCSCS${sale.receipt_no.replace("RCS", "")}`
        : `RCSCR${sale.receipt_no.replace("RCS", "")}`;

    const base = [
      receipt,
      sale.customer?.name || sale.customer_name || "Unknown",
      sale.payment_method,
    ];

    // 👇 Insert cashier (for admins)
    if (isAdmin()) {
      const cashier =
        sale.cashier_name ||
        sale.cashier_email ||
        (sale.cashier_id ? `#${sale.cashier_id}` : "Unknown");
      base.push(cashier);
    }

    return [
      ...base,
      itemText || "—",
      `$${parseFloat(sale.subtotal).toFixed(2)}`,
      `-$${parseFloat(sale.discount).toFixed(2)}`,
      `$${parseFloat(sale.total).toFixed(2)}`,
      new Date(sale.date_of_transaction).toLocaleDateString("en-US"),
      new Date(sale.created_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    ];
  });

  // ✅ Totals
  const totalSubtotal = filteredSales.reduce(
    (sum, sale) => sum + parseFloat(sale.subtotal || 0),
    0
  );
  const totalTotal = filteredSales.reduce(
    (sum, sale) => sum + parseFloat(sale.total || 0),
    0
  );

  // ✅ Add totals row at the end (position depends on whether Cashier column exists)
  if (isAdmin()) {
    rows.push([
      "TOTALS", "", "", "", "",                     // up to "Items"
      `$${totalSubtotal.toFixed(2)}`,               // Subtotal
      "",                                           // Discount
      `$${totalTotal.toFixed(2)}`,                  // Total
      "", "",                                       // Date, Time
    ]);
  } else {
    rows.push([
      "TOTALS", "", "", "",                         // up to "Items"
      `$${totalSubtotal.toFixed(2)}`,               // Subtotal
      "",                                           // Discount
      `$${totalTotal.toFixed(2)}`,                  // Total
      "", "",                                       // Date, Time
    ]);
  }

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows].map((e) => e.join(",")).join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "sales_history.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  
  const deleteSale = async (id) => {
    if (!isAdmin()) { toast.error("You don't have permission to delete sales."); return; }
    const confirmed = await confirm("Are you sure you want to delete this sale?");
    if (!confirmed) return;
    
  
    try {
  const response = await fetch(api(`/sales/${id}`), {
    method: "DELETE",
   headers: { ...authHeader() },
   });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete sale: ${errorText}`);
      }
  
      toast.success("🗑️ Sale deleted successfully!");
  
      // Remove sale from state
      setSales((prevSales) => prevSales.filter((sale) => sale.id !== id));
  
    } catch (err) {
      console.error("Delete Sale Error:", err);
      toast.error(`❌ Failed to delete sale. ${err.message}`);
    }
  };
  

  const handleRefund = async (sale) => {
    // backend allows owner or admin; UI-wise it's safer to block non-admin here
if (!isAdmin()) { toast.error("You don't have permission to refund sales."); return; }
    const confirmed = await confirm(`Refund sale ${sale.receipt_no}?`);
if (!confirmed) return;

  
    try {
   const res = await fetch(api(`/sales/${sale.id}/refund`), {
     method: "PATCH",
     headers: { ...authHeader() },
  });
  
      if (!res.ok) throw new Error("Refund failed");
  
      toast.success("💸 Refund processed successfully!");
      
      // ✅ Refresh state
      fetchSales();
    } catch (err) {
      console.error("Refund error:", err);
      toast.error("❌ Failed to process refund.");
    }
  };
  
  

const getRecentSalesSummary = (sales) => {
  const today = new Date();
  const recentDays = Array.from({ length: 4 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    return d.toISOString().split("T")[0];
  }).reverse();

  const summaryMap = recentDays.reduce((acc, date) => {
    acc[date] = { income: 0, payout: 0 };
    return acc;
  }, {});

  sales.forEach((sale) => {
    const raw = sale.date_of_transaction || sale.created_at;
    if (!raw) return;
    const saleDate = new Date(raw).toISOString().split("T")[0];
    if (!(saleDate in summaryMap)) return;

    let items = [];
    try {
      items = typeof sale.items === "string" && sale.items.trim().startsWith("[")
        ? JSON.parse(sale.items)
        : sale.items;
    } catch {
      return;
    }

    items?.forEach((item) => {
      const qty = parseFloat(item.quantity || 1);
      const price = parseFloat(item.price || 0);
      const value = price * qty;
      if (value > 0) summaryMap[saleDate].income += value;
      else if (value < 0) summaryMap[saleDate].payout += Math.abs(value);
    });
  });

  return Object.entries(summaryMap).map(([date, { income, payout }]) => ({
    date: new Date(date).toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric"
    }),
    total: parseFloat((income - payout).toFixed(2)),
    income: parseFloat(income.toFixed(2)),
    payout: parseFloat(payout.toFixed(2)),
  }));
};



/* =====================================================
    INCOMING SALES ONLY
===================================================== */

const incomingSales =
  filteredSales.filter(
    (sale) => {

      const total =
        parseFloat(
          sale.total || 0
        );

      return (
        sale.status !==
          "refunded" &&
        total > 0
      );
    }
  );

/* =====================================================
    OUTGOING SALES
===================================================== */

const outgoingSales =
  filteredSales.filter(
    (sale) => {

      try {

        let items = [];

        if (
          typeof sale.items ===
          "string"
        ) {

          items =
            sale.items.trim().startsWith("[")
              ? JSON.parse(
                  sale.items
                )
              : [];

        } else if (
          Array.isArray(
            sale.items
          )
        ) {

          items = sale.items;

        }

        // CHECK FOR NEGATIVE ITEMS
        return items.some(
          (item) => {

            const qty =
              parseFloat(
                item.quantity || 1
              );

            const price =
              parseFloat(
                item.price || 0
              );

            return (
              qty * price < 0
            );

          }
        );

      } catch {

        return false;

      }

    }
  );

  /* =====================================================
    REFUNDED SALES
===================================================== */

const refundedSales =
  filteredSales.filter(
    (sale) =>
      sale.status ===
      "refunded"
  );

/* =====================================================
    TRUE INCOMING REVENUE
===================================================== */

const incomingRevenue =
  incomingSales.reduce(
    (sum, sale) =>
      sum +
      parseFloat(
        sale.total || 0
      ),
    0
  );

/* =====================================================
    TRUE OUTGOING REVENUE
===================================================== */

const outgoingRevenue =
  outgoingSales.reduce(
    (sum, sale) => {

      try {

        let items = [];

        if (
          typeof sale.items ===
          "string"
        ) {

          items =
            sale.items.trim().startsWith("[")
              ? JSON.parse(
                  sale.items
                )
              : [];

        } else if (
          Array.isArray(
            sale.items
          )
        ) {

          items = sale.items;

        }

        const negativeTotal =
          items.reduce(
            (
              itemSum,
              item
            ) => {

              const qty =
                parseFloat(
                  item.quantity || 1
                );

              const price =
                parseFloat(
                  item.price || 0
                );

              const value =
                qty * price;

              if (value < 0) {

                return (
                  itemSum +
                  Math.abs(
                    value
                  )
                );

              }

              return itemSum;

            },
            0
          );

        return (
          sum + negativeTotal
        );

      } catch {

        return sum;

      }

    },
    0
  );
/* =====================================================
    COUNTS
===================================================== */

const incomingCount =
  incomingSales.length;

const outgoingCount =
  outgoingSales.length;

/* =====================================================
    TRUE AVERAGE
===================================================== */

const averageSale =
  incomingCount > 0
    ? incomingRevenue /
      incomingCount
    : 0;

/* =====================================================
    PAYMENT COUNTS
===================================================== */

const cashCount =
  incomingSales.filter(
    (sale) =>
      sale.payment_method ===
      "Cash"
  ).length;

const creditCount =
  incomingSales.filter(
    (sale) =>
      sale.payment_method ===
      "Credit"
  ).length;

/* =====================================================
    PAGINATION
===================================================== */

const SALES_PER_PAGE = 20;

const totalPages = Math.ceil(
  filteredSales.length /
    SALES_PER_PAGE
);

const paginatedSales =
  filteredSales.slice(
    (currentPage - 1) *
      SALES_PER_PAGE,

    currentPage *
      SALES_PER_PAGE
  );

const refundCount =
  filteredSales.filter(
    (sale) =>
      sale.status ===
      "refunded"
  ).length;

return (
  <div className="modern-sales-page">

    {/* =====================================================
        HERO
    ===================================================== */}
    <div className="modern-sales-hero">

      <div className="modern-sales-hero-top">

        <div>

          <div className="modern-sales-pill">
            Sales Intelligence
          </div>

          <h1>
            Sales Dashboard
          </h1>

          <p>
            Monitor incoming revenue,
            outgoing adjustments,
            transaction activity and
            operational performance.
          </p>

        </div>

        <div className="modern-sales-top-actions">

          {isAdmin() && (

            <button
              className="modern-scope-btn"
              onClick={() =>
                setViewMine((v) => !v)
              }
            >

              {viewMine
                ? "Show All Sales"
                : "Show My Sales"}

            </button>

          )}

        </div>

      </div>

      {/* =====================================================
          KPI GRID
      ===================================================== */}
      <div className="modern-sales-kpi-grid">

        {/* =====================================================
            INCOMING REVENUE
        ===================================================== */}
        <div className="modern-sales-kpi-card">

          <div className="modern-sales-kpi-icon teal">
            $
          </div>

          <div>

            <span>
              Incoming Revenue
            </span>

            <h2>
              $
              {incomingRevenue.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

            <p>
              {incomingCount}
              {" "}
              completed transactions
            </p>

          </div>

        </div>

        {/* =====================================================
            OUTGOING REVENUE
        ===================================================== */}
        <div className="modern-sales-kpi-card">

          <div className="modern-sales-kpi-icon blue">
            ↗
          </div>

          <div>

            <span>
              Outgoing Revenue
            </span>

            <h2>
              $
              {outgoingRevenue.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

            <p>
              {outgoingCount}
              {" "}
              refunds / reversals
            </p>

          </div>

        </div>

        {/* =====================================================
            REFUNDS
        ===================================================== */}
        <div className="modern-sales-kpi-card">

          <div className="modern-sales-kpi-icon red">
            ↩
          </div>

          <div>

            <span>
              Refunded Sales
            </span>

            <h2>
              {refundCount}
            </h2>

            <p>
              Refunded receipts
            </p>

          </div>

        </div>

        {/* =====================================================
            AVERAGE SALE
        ===================================================== */}
        <div className="modern-sales-kpi-card">

          <div className="modern-sales-kpi-icon green">
            📈
          </div>

          <div>

            <span>
              Average Sale
            </span>

            <h2>
              $
              {averageSale.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

            <p>
              Average incoming sale
            </p>

          </div>

        </div>

      </div>

    </div>

    {/* =====================================================
        FILTER TOOLBAR
    ===================================================== */}
    <div className="modern-sales-toolbar">

      {/* SEARCH */}
      <input
        type="text"
        value={searchTerm}
        onChange={(e) =>
          setSearchTerm(
            e.target.value
          )
        }
        placeholder="Search receipt, customer or barcode..."
        className="modern-sales-search"
      />

      {/* FILTERS */}
      <div className="modern-sales-toolbar-group">

        {/* PAYMENT */}
        <select
          value={paymentFilter}
          onChange={(e) =>
            setPaymentFilter(
              e.target.value
            )
          }
        >

          <option value="All">
            All Payments
          </option>

          <option value="Cash">
            Cash
          </option>

          <option value="Credit">
            Credit
          </option>

        </select>

        {/* DATE */}
        <DatePicker
          selected={
            dateFilter
              ? new Date(
                  dateFilter +
                    "T00:00:00"
                )
              : null
          }
          onChange={(date) => {

            if (date) {

              const local =
                new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate()
                );

              const formatted =
                local
                  .toISOString()
                  .split("T")[0];

              setDateFilter(
                formatted
              );

            } else {

              setDateFilter("");

            }
          }}
          dateFormat="yyyy-MM-dd"
          className="modern-sales-datepicker"
        />

        {/* APPLY */}
        <button
          className="modern-toolbar-btn"
          onClick={() => {

            setAppliedPaymentFilter(
              paymentFilter
            );

            setAppliedDateFilter(
              dateFilter
            );

          }}
        >
          Apply
        </button>

        {/* EXPORT PDF */}
        <button
          className="modern-toolbar-btn"
          onClick={
            handleExportPDF
          }
        >
          PDF
        </button>

        {/* EXPORT CSV */}
        <button
          className="modern-toolbar-btn"
          onClick={
            handleExportCSV
          }
        >
          CSV
        </button>

      </div>

    </div>

    {/* =====================================================
        ANALYTICS GRID
    ===================================================== */}
    <div className="modern-sales-grid">

      {/* =====================================================
          LINE GRAPH
      ===================================================== */}
      <div className="modern-sales-chart-card">

        <div className="modern-sales-card-header">

          <div>

            <h3>
              Incoming vs Outgoing Transactions
            </h3>

            <span>
              Operational transaction
              flow over recent days
            </span>

          </div>

        </div>

        <div
          style={{
            width: "100%",
            height: 340,
          }}
        >

          <ResponsiveContainer>

            <LineChart
              data={getRecentSalesSummary(
                sales
              )}
            >

              <CartesianGrid
                strokeDasharray="3 3"
                opacity={0.12}
              />

              <XAxis dataKey="date" />

              <YAxis />

              <Tooltip />

              {/* INCOMING */}
              <Line
                type="monotone"
                dataKey="income"
                stroke="#14b8a6"
                strokeWidth={4}
                dot={{
                  r: 6,
                  fill: "#14b8a6",
                }}
                activeDot={{
                  r: 8,
                }}
              />

              {/* OUTGOING */}
              <Line
                type="monotone"
                dataKey="payout"
                stroke="#ef4444"
                strokeWidth={4}
                dot={{
                  r: 6,
                  fill: "#ef4444",
                }}
                activeDot={{
                  r: 8,
                }}
              />

            </LineChart>

          </ResponsiveContainer>

        </div>

      </div>

      {/* =====================================================
          SIDEBAR
      ===================================================== */}
      <div className="modern-sales-side-panel">

        {/* =====================================================
            PAYMENT BREAKDOWN
        ===================================================== */}
        <div className="modern-side-card">

          <h3>
            Payment Breakdown
          </h3>

          <div className="modern-payment-breakdown">

            {/* CASH */}
            <div className="modern-payment-row">

              <span>
                Cash
              </span>

              <strong>
                {cashCount}
              </strong>

            </div>

            {/* CREDIT */}
            <div className="modern-payment-row">

              <span>
                Credit
              </span>

              <strong>
                {creditCount}
              </strong>

            </div>

          </div>

        </div>

        {/* =====================================================
            RECENT TRANSACTIONS
        ===================================================== */}
        <div className="modern-side-card">

          <div className="modern-sales-card-header">

            <h3>
              Recent Transactions
            </h3>

          </div>

          <div className="modern-recent-sales">

            {filteredSales
              .slice(0, 5)
              .map((sale) => (

                <div
                  key={sale.id}
                  className="modern-recent-sale-row"
                >

                  <div>

                    <strong>
                      {
                        sale.receipt_no
                      }
                    </strong>

                    <span>
                      {
                        sale.customer_name
                      }
                    </span>

                  </div>

                  <div>

                    <strong>
                      $
                      {parseFloat(
                        sale.total
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </strong>

                  </div>

                </div>

              ))}

          </div>

        </div>

      </div>

    </div>

    {/* =====================================================
        SALES TABLE
    ===================================================== */}
    <div className="modern-sales-table-card">

      <div className="modern-sales-card-header">

        <div>

          <h3>
            Sales History
          </h3>

          <span>
            Showing recent
            transaction records
          </span>

        </div>

      </div>

      {loading ? (

        <div className="modern-sales-loading">

          Loading sales...

        </div>

      ) : (

        <>

          <div className="modern-sales-table-wrap">

            <table className="modern-sales-table">

              <thead>

                <tr>

                  <th>
                    Receipt
                  </th>

                  <th>
                    Customer
                  </th>

                  <th>
                    Payment
                  </th>

                  {isAdmin() && (
                    <th>
                      Cashier
                    </th>
                  )}

                  <th>
                    Total
                  </th>

                  <th>
                    Date
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Actions
                  </th>

                </tr>

              </thead>

              <tbody>

                {paginatedSales.map(
                  (sale) => {

                    const formattedDate =
                      new Date(
                        sale.created_at
                      ).toLocaleDateString(
                        "en-US"
                      );

                    return (

                      <tr
                        key={sale.id}
                      >

                        {/* RECEIPT */}
                        <td className="modern-sales-receipt">

                          {
                            sale.receipt_no
                          }

                        </td>

                        {/* CUSTOMER */}
                        <td>

                          <div className="modern-sales-customer">

                            <div className="modern-sales-avatar">

                              {sale.customer_name?.charAt(
                                0
                              )}

                            </div>

                            <span>
                              {
                                sale.customer_name
                              }
                            </span>

                          </div>

                        </td>

                        {/* PAYMENT */}
                        <td>

                          <span className="modern-payment-pill">

                            {
                              sale.payment_method
                            }

                          </span>

                        </td>

                        {/* CASHIER */}
                        {isAdmin() && (

                          <td>

                            {displayCashier(
                              sale
                            )}

                          </td>

                        )}

                        {/* TOTAL */}
                        <td className="modern-sales-total">

                          $
                          {parseFloat(
                            sale.total
                          ).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                            }
                          )}

                        </td>

                        {/* DATE */}
                        <td>

                          {
                            formattedDate
                          }

                        </td>

                        {/* STATUS */}
                        <td>

                          <span
                            className={`modern-sales-status ${
                              sale.status ===
                              "refunded"
                                ? "refund"
                                : "complete"
                            }`}
                          >

                            {sale.status ===
                            "refunded"
                              ? "Refunded"
                              : "Completed"}

                          </span>

                        </td>

                        {/* ACTIONS */}
                        <td>

                          <div className="modern-sales-actions">

                            {/* PRINT */}
                            <button
                              className="modern-action-btn print"
                              onClick={async () => {

                                try {

                                  const res =
                                    await fetch(
                                      api(
                                        `/sales/${sale.id}`
                                      ),
                                      {
                                        headers:
                                          {
                                            ...authHeader(),
                                            "Content-Type":
                                              "application/json",
                                          },
                                      }
                                    );

                                  const fullSale =
                                    await res.json();

                                  const matchedCustomer =
                                    customerDatabase.find(
                                      (
                                        cust
                                      ) =>
                                        cust.name ===
                                        fullSale.customer_name
                                    ) || {
                                      name:
                                        fullSale.customer_name ||
                                        "Unknown",
                                    };

                                  await printReceipt(
                                    {
                                      ...fullSale,
                                      customer:
                                        matchedCustomer,
                                    }
                                  );

                                } catch (
                                  err
                                ) {

                                  toast.error(
                                    "Print failed."
                                  );

                                }
                              }}
                            >
                              🖨
                            </button>

                            {/* REFUND */}
                            {isAdmin() && (

                              <button
                                className="modern-action-btn refund"
                                onClick={() =>
                                  handleRefund(
                                    sale
                                  )
                                }
                              >
                                ↩
                              </button>

                            )}

                            {/* DELETE */}
                            {isAdmin() && (

                              <button
                                className="modern-action-btn delete"
                                onClick={() =>
                                  deleteSale(
                                    sale.id
                                  )
                                }
                              >
                                🗑
                              </button>

                            )}

                          </div>

                        </td>

                      </tr>

                    );
                  }
                )}

              </tbody>

            </table>

          </div>

          {/* =====================================================
              PAGINATION
          ===================================================== */}
          <div className="modern-sales-pagination">

            <button
              disabled={
                currentPage === 1
              }
              onClick={() =>
                setCurrentPage(
                  (p) =>
                    Math.max(
                      p - 1,
                      1
                    )
                )
              }
            >
              Previous
            </button>

            <span>

              Page {currentPage}
              {" "}
              of
              {" "}
              {totalPages || 1}

            </span>

            <button
              disabled={
                currentPage ===
                  totalPages ||
                totalPages === 0
              }
              onClick={() =>
                setCurrentPage(
                  (p) =>
                    Math.min(
                      p + 1,
                      totalPages
                    )
                )
              }
            >
              Next
            </button>

          </div>

        </>

      )}

    </div>

  </div>
);
};

export default SalesHistory;
